<template>
  <div class="modal" @keydown.esc="$emit('close')">
    <div class="modal-content">
      <form @submit.prevent="submitShift">
        <div class="modal-header">
          <h1>Create shift</h1>
          <button type="button" class="btn-close" data-bs-dismiss="modal" @click="$emit('close')"
                  aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="card text-white bg-secondary mb-3">
            <div class="card-body mb-0">
              <h2 v-if="event">Event Details</h2>
              <dl v-if="event" class="row">
                <dt class="col-sm-3">Name</dt>
                <dd class="col-sm-9">{{ event.name }}</dd>
                <dt class="col-sm-3">Date</dt>
                <dd class="col-sm-9">{{ formatDate(event.date) }}</dd>
              </dl>
            </div>
          </div>
          <h2>Shift Details</h2>
          <div class="mb-3">
            <label for="name" class="form-label">Name</label>
            <input type="text" class="form-control" id="name" v-model="shiftData.name" ref="nameInput" required
                   placeholder="Shift name"/>
          </div>
          <div class="mb-3">
            <label for="location" class="form-label">Location</label>
            <div v-for="location in sortedLocations" :key="location.id" class="form-check">
              <input class="form-check-input" type="radio" :id="'location-' + location.id" :value="location.id"
                     v-model="shiftData.location" required>
              <label class="form-check-label" :for="'location-' + location.id">
                {{ location.name }}
              </label>
            </div>
          </div>
          <div class="mb-3">
            <label for="capacity" class="form-label">Capacity</label>
            <input type="number" class="form-control" id="capacity" v-model="shiftData.capacity" required
                   placeholder="Shift capacity"/>
          </div>
          <div class="mb-3">
            <label for="start_datetime" class="form-label">Start date and time</label>
            <input type="datetime-local" class="form-control" id="start_datetime" v-model="shiftData.start" required
                   placeholder="Shift start date and time"/>
          </div>
          <div class="mb-3">
            <label for="end_datetime" class="form-label">End date and time</label>
            <input type="datetime-local" class="form-control" id="end_datetime" v-model="shiftData.end" required
                   placeholder="Shift end date and time"/>
          </div>
          <div class="card text-white bg-secondary mb-3">
            <div class="card-body mb-0">
              <dl class="row">
                <dt class="col-sm-3">Shift length</dt>
                <dd class="col-sm-9">{{ shiftLength() }} hours</dd>
              </dl>
            </div>
          </div>

          <!-- Tag -->
          <div class="mb-3">
            <label for="tag" class="form-label">Tag</label>
            <select class="form-control" v-model="shiftData.tag" id="tag">
              <option v-for="tag in tags" :key="tag.value" :value="tag.value">{{ tag.label }}</option>
            </select>
          </div>

          <!-- Score -->
          <div class="mb-3">
            <label for="score" class="form-label">Score</label>
            <input type="number" class="form-control" id="score" v-model="shiftData.score" required
                   placeholder="Shift score" min="0"/>
          </div>

        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="$emit('close')">Cancel
          </button>
          <button type="submit" class="btn btn-primary" :disabled="isSubmitButtonDisabled">Create shift</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import duration from 'dayjs/plugin/duration';

dayjs.extend(utc);
dayjs.extend(duration);

export default {
  name: 'CreateShiftModal',
  props: {
    event: {
      type: Object,
      required: true,
    },
    locations: {
      type: Array,
      required: true,
    },
    // All the shifts in the organisation
    organisation_shifts: {
      type: Array,
      required: true,
    },
    tags: {
      type: Array,
      required: true,
      default: () => [],
    },
  },

  data() {
    return {
      shiftData: {
        name: '',
        capacity: '',
        location: null,
        start: '',
        end: '',
        score: 3,
        tag: '',
      },
      startDateTime: '',
      endDateTime: '',
    };
  },

  computed: {
    sortedLocations() {
      return this.locations.slice().sort((a, b) => a.code.localeCompare(b.code));
    },
    isSubmitButtonDisabled() {
      return this.shiftData.name === '' || this.shiftData.capacity === '' || this.shiftData.location === null;
    },
  },

  watch: {
    'shiftData.start': 'shiftLength',
    'shiftData.end': 'shiftLength',
    'shiftData.location': "suggestedStart",
  },

  mounted() {
    this.$refs.nameInput.focus();
    this.setStartAndEndDateTime();
  },

  methods: {
    setStartAndEndDateTime(startDate = this.event.date, startHour = this.event.default_shift_start_time, shiftLengthHoursMinutesSeconds = this.event.default_shift_duration) {
      console.log("setStartAndEndDateTime", startDate, startHour, shiftLengthHoursMinutesSeconds);
      // Create time zone aware datetime object
      const startDateTime = dayjs(`${startDate}T${startHour}`);
      // Drop the time zone information for use in the datetime-local input field
      this.shiftData.start = startDateTime.format('YYYY-MM-DDTHH:mm');

      // Split shiftLengthHoursMinutesSeconds into hours, minutes and seconds
      const [hours, minutes] = shiftLengthHoursMinutesSeconds.split(':').map(Number);

      // Add hours, minutes and seconds to the start time to get the end time
      const endDate = startDateTime.add(hours, 'hour').add(minutes, 'minute');
      // Drop the time zone information for use in the datetime-local input field
      this.shiftData.end = endDate.format('YYYY-MM-DDTHH:mm');
    },
    submitShift() {
      // Set the start and end values in UTC mode and formatted correctly
      this.shiftData.start = dayjs(this.shiftData.start).utc().format('YYYY-MM-DDTHH:mm');
      this.shiftData.end = dayjs(this.shiftData.end).utc().format('YYYY-MM-DDTHH:mm');
      this.$emit('create-shift', this.shiftData);
    },
    formatDate(date) {
      return dayjs(date).format('dddd DD/MM/YYYY');
    },
    suggestedStart() {
      // Search for shifts on the same location of the selected event
      let matching_event_shifts = this.event.shifts.filter(shift => shift.location.id === this.shiftData.location);

      // If there are no shifts yet found in the current event,
      // search for shifts of the current organisation in the same location,
      // and return those shifts that have an end date that is the same as the current event's date.
      if (matching_event_shifts.length === 0) {
        // Find all the shifts in the organisation with matching location and end date
        let matching_organisation_shifts_by_location_and_end_date = this.organisation_shifts.filter(shift =>
            shift.location.id === this.shiftData.location &&
            dayjs(shift.end).local().format('YYYY-MM-DD') === this.event.date);
        if (matching_organisation_shifts_by_location_and_end_date.length === 0) {
          this.setStartAndEndDateTime();
          this.shiftData.previous_shift = null;
          return dayjs(`${this.event.date}T${this.event.default_shift_start_time}`).format('YYYY-MM-DD HH:mm');
        }

        // Get the shift with the latest end date
        const matching_organisation_shift = matching_organisation_shifts_by_location_and_end_date.reduce((a, b) => (dayjs(a.end) > dayjs(b.end) ? a : b));
        this.shiftData.previous_shift = matching_organisation_shift.id;
        const endTimeLocal = dayjs(matching_organisation_shift.end).local();
        this.setStartAndEndDateTime(endTimeLocal.format('YYYY-MM-DD'), endTimeLocal.format('HH:mm'), this.event.default_shift_duration);
        return dayjs(matching_organisation_shift.end).format('YYYY-MM-DDTHH:mm');
      }

      // Get the shift with the latest end date
      const latestShift = matching_event_shifts.reduce((a, b) => (dayjs(a.end) > dayjs(b.end) ? a : b));
      this.shiftData.previous_shift = latestShift.id;
      // Return the end date of the latest shift

      console.log("latest Shift End - TZ aware:", latestShift.end);
      // Use dayjs to convert the time zone aware datetime object to a local datetime object
      const endTimeLocal = dayjs(latestShift.end).local();
      console.log("latest Shift End - local:", endTimeLocal);

      this.setStartAndEndDateTime(endTimeLocal.format('YYYY-MM-DD'), endTimeLocal.format('HH:mm'), this.event.default_shift_duration);

      return dayjs(latestShift.end).format('YYYY-MM-DDTHH:mm');
    },
    shiftLength() {
      if (this.shiftData.start && this.shiftData.end) {
        const start = dayjs(this.shiftData.start);
        const end = dayjs(this.shiftData.end);
        return dayjs.duration(end.diff(start)).asHours().toFixed(2);
      }
      return "/";
    },
  },
};
</script>


<style scoped>
.modal {
  display: block;
  position: fixed;
  z-index: 1;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgb(0, 0, 0);
  background-color: rgba(0, 0, 0, 0.4);
}

.modal-content {
  background-color: #fefefe;
  margin: 15% auto;
  padding: 20px;
  border: 1px solid #888;
  width: 80%;
}
</style>